import { useLogto } from '@logto/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useActiveConversation } from '../../contexts/ActiveConversationContext';
import { useMobileDrawer } from '../../contexts/MobileDrawerContext';
import { type ThemeOverride, useTheme } from '../../contexts/ThemeContext';
import { LANG_FONTS, LANG_LABELS, type SupportedLang, SUPPORTED_LANGS } from '../../i18n';
import { ConversationList } from '../conversations/ConversationList';
import { IconClose, IconPlus, IconSignOut } from '../primitives/Icons';

const THEME_OPTIONS: Array<{ value: ThemeOverride; labelKey: string }> = [
  { value: 'auto', labelKey: 'themeAuto' },
  { value: 'morning', labelKey: 'themeMorning' },
  { value: 'afternoon', labelKey: 'themeAfternoon' },
  { value: 'evening', labelKey: 'themeEvening' },
];

export function LeftSidebar() {
  const { t, i18n } = useTranslation();
  const { override, setOverride } = useTheme();
  const { runState } = useActiveConversation();
  const { isOpen, close, desktopCollapsed } = useMobileDrawer();
  const { signOut, getIdTokenClaims } = useLogto();
  const navigate = useNavigate();

  const [userName, setUserName] = useState<string>('');

  useEffect(() => {
    void getIdTokenClaims().then((c) => {
      setUserName(c?.name || c?.username || c?.email || 'User');
    });
  }, [getIdTokenClaims]);

  const isStreaming = runState === 'streaming' || runState === 'sending';

  const handleSignOut = () => {
    void signOut(window.location.origin);
  };

  const handleLangChange = (lang: SupportedLang) => {
    void i18n.changeLanguage(lang);
    document.body.style.setProperty('--ff', LANG_FONTS[lang]);
    document.documentElement.lang = lang;
  };

  const handleNewChat = () => {
    navigate('/c/new');
    close();
  };

  const className = [
    'sidebar-left',
    isOpen ? 'mobile-open' : '',
    desktopCollapsed ? 'collapsed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className}>
      <div className="sidebar-logo">
        <div className="logo-icon">Z</div>
        <span className="logo-text">{t('appName')}</span>
        <button
          type="button"
          className="sidebar-close-btn mobile-only"
          onClick={close}
          aria-label="Close"
        >
          <IconClose />
        </button>
      </div>

      <button
        type="button"
        className="new-chat-btn"
        disabled={isStreaming}
        onClick={handleNewChat}
        title={t('newChat')}
      >
        <IconPlus />
        <span>{t('newChat')}</span>
      </button>

      <ConversationList />

      <div className="sidebar-bottom">
        <div className="theme-switcher">
          {THEME_OPTIONS.map((o) => (
            <button
              type="button"
              key={o.value}
              className={`theme-btn${override === o.value ? ' active' : ''}`}
              onClick={() => setOverride(o.value)}
              title={t(o.labelKey)}
            >
              {t(o.labelKey)}
            </button>
          ))}
        </div>

        <div className="lang-switcher">
          {SUPPORTED_LANGS.map((l) => (
            <button
              type="button"
              key={l}
              className={`lang-btn${i18n.resolvedLanguage === l ? ' active' : ''}`}
              onClick={() => handleLangChange(l)}
            >
              {LANG_LABELS[l]}
            </button>
          ))}
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">{(userName[0] || 'U').toUpperCase()}</div>
          <div className="user-info">
            <div className="user-name" title={userName}>
              {userName || 'User'}
            </div>
            <div className="user-role">{t('freePlan')}</div>
          </div>
          <button
            type="button"
            className="signout-btn"
            onClick={handleSignOut}
            title={t('signOut')}
            aria-label={t('signOut')}
          >
            <IconSignOut />
          </button>
        </div>
      </div>
    </div>
  );
}
